import React, { useRef, useMemo, useState } from "react";
import * as THREE from 'three'
import { useGLTF } from "@react-three/drei";
import { useLoader, useFrame } from '@react-three/fiber'
import { TextureLoader } from 'three/src/loaders/TextureLoader'
import Label from './Label'


export function Model(props)
{

    console.log("model rendered!")

    const delay = props.delay
    const scale = props.scale
    const selectedShardsScale = props.selectedShardsScale
    const speed = props.speed
    const selectedShards = props.selectedShards

    const cylinderGroupRef = useRef(null)
    const experienceRef = useRef(null)
    const occluderRef = useRef(null)

    const colorMap = useMemo(() => useLoader(TextureLoader, require('../textures/cylinder_diffuse_baked_version_6_optim.jpg')))
    colorMap.flipY = false

    const outsideMat = useMemo(() => new THREE.MeshStandardMaterial({ map: colorMap, roughness: 1, metalness: 0, }))
    const insideMat = useMemo(() => new THREE.MeshStandardMaterial({ color: "#57462D" }))

    const [labelsVisible, setLabelsVisible] = useState(false)

    // helper functions
    function lerp(start, end, percent)
    {
        return (start + (end - start) * percent)
    }

    function easeInOut(t)
    {
        return t > 0.5 ? 4 * Math.pow((t - 1), 3) + 1 : 4 * Math.pow(t, 3);
    }

    // import model
    const { nodes } = useGLTF(
        require("../models/cyrus_cylinder_low_poly_version_6.glb")
    );

    // store model loaded condition
    let initialPositionsLoaded = false

    const initialPositions = useMemo(() =>
    {
        const tempArray = []
        nodes.Scene.children.forEach((element, index) =>
        {
            tempArray.push({ x: element.position.x, y: element.position.y, z: element.position.z })
        });
        initialPositionsLoaded = true
        return tempArray
    })

    useFrame((state, delta) =>
    {
        const time = state.clock.elapsedTime - delay
        if (initialPositionsLoaded)
        {
            cylinderGroupRef.current.children.forEach((element, index) =>
            {
                const lerpTime = Math.max(Math.min(easeInOut(time * speed), 1), 0)

                let tempScale = scale
                let tempScaleX = 1

                if (selectedShards.includes(index))
                {
                    tempScale = selectedShardsScale
                    tempScaleX = 0.8
                }

                element.position.x = lerp(initialPositions[index].x, initialPositions[index].x * tempScale * tempScaleX, lerpTime)
                element.position.y = lerp(initialPositions[index].y, initialPositions[index].y * tempScale, lerpTime)
                element.position.z = lerp(initialPositions[index].z, initialPositions[index].z * tempScale, lerpTime)

                element.rotation.x = lerp(0, (initialPositions[index].x - element.position.x * tempScale) * 0.3, lerpTime)
                element.rotation.y = lerp(0, (initialPositions[index].y - element.position.y * tempScale) * 0.6, lerpTime)
            });
        }

        const tempScale = Math.min((state.size.width / state.size.height) * 0.9, 1)
        experienceRef.current.scale.x = tempScale
        experienceRef.current.scale.y = tempScale
        experienceRef.current.scale.z = tempScale

        // report loaded
        props.setModelLoaded(true)

        setTimeout(() => { setLabelsVisible(true) }, (props.delay * 1000) + 3000)

    })

    return (
        <group scale={[1, 1, 1]} {...props} ref={experienceRef}>
            {
                selectedShards.map((selectedShard, i) =>
                {
                    const x = initialPositions[selectedShard].x
                    const y = initialPositions[selectedShard].y
                    const z = initialPositions[selectedShard].z
                    return <Label
                        debug={window.location.href.indexOf("#debug") > -1}
                        shardNumber={selectedShard}

                        occlude={occluderRef}
                        visible={labelsVisible}
                        key={"label_" + i}
                        position={[x * selectedShardsScale * 0.8, y * selectedShardsScale, z * selectedShardsScale]}

                        setArticleOpen={props.setArticleOpen}
                        articleIndex={i}
                        setCurrentArticleIndex={props.setCurrentArticleIndex}
                    />
                })
            }

            <mesh ref={occluderRef}>
                <boxGeometry args={[4.5, 1.6, 1.4]} />
                <meshBasicMaterial opacity={0} transparent />
            </mesh>

            {/* <Label /> */}
            <group ref={cylinderGroupRef} >

                <group position={[-2.065, 0.553, 0.231]}>
                    <mesh
                        geometry={nodes.lowPol_cell050.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell050_1.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-0.67, 0.12, -0.717]}>
                    <mesh
                        geometry={nodes.lowPol_cell051.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell051_1.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-1.39, 0.848, 0.045]}>
                    <mesh
                        geometry={nodes.lowPol_cell001_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell001_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[1.648, -0.612, -0.369]}>
                    <mesh
                        geometry={nodes.lowPol_cell002_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell002_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[0.936, 0.512, 0.65]}>
                    <mesh
                        geometry={nodes.lowPol_cell003_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell003_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-2.051, 0.539, -0.107]}>
                    <mesh
                        geometry={nodes.lowPol_cell004_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell004_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[1.015, -0.693, -0.321]}>
                    <mesh
                        geometry={nodes.lowPol_cell005_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell005_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-0.399, 0.889, 0.091]}>
                    <mesh
                        geometry={nodes.lowPol_cell006_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell006_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[0.075, 0.847, 0.446]}>
                    <mesh
                        geometry={nodes.lowPol_cell007_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell007_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-0.69, -0.178, 0.723]}>
                    <mesh
                        geometry={nodes.lowPol_cell008_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell008_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[1.684, -0.582, 0.345]}>
                    <mesh
                        geometry={nodes.lowPol_cell009_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell009_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-1.778, -0.525, -0.173]}>
                    <mesh
                        geometry={nodes.lowPol_cell010_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell010_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[1.872, -0.173, -0.485]}>
                    <mesh
                        geometry={nodes.lowPol_cell011_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell011_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[1.068, -0.72, 0.111]}>
                    <mesh
                        geometry={nodes.lowPol_cell012_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell012_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-0.896, -0.571, -0.258]}>
                    <mesh
                        geometry={nodes.lowPol_cell013_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell013_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-1.753, 0.244, 0.563]}>
                    <mesh
                        geometry={nodes.lowPol_cell014.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell014_1.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[0.969, -0.433, 0.624]}>
                    <mesh
                        geometry={nodes.lowPol_cell015_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell015_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-1.296, -0.385, 0.398]}>
                    <mesh
                        geometry={nodes.lowPol_cell016_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell016_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-1.467, -0.32, -0.66]}>
                    <mesh
                        geometry={nodes.lowPol_cell017_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell017_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[0.499, -0.357, 0.731]}>
                    <mesh
                        geometry={nodes.lowPol_cell018_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell018_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[1.796, 0.163, -0.727]}>
                    <mesh
                        geometry={nodes.lowPol_cell019_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell019_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[1.549, 0.347, 0.679]}>
                    <mesh
                        geometry={nodes.lowPol_cell020_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell020_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[0.455, 0.407, 0.749]}>
                    <mesh
                        geometry={nodes.lowPol_cell021_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell021_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[0.317, 0.774, -0.35]}>
                    <mesh
                        geometry={nodes.lowPol_cell022.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell022_1.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-1.148, 0.508, -0.663]}>
                    <mesh
                        geometry={nodes.lowPol_cell023_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell023_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[0.155, 0.271, -0.72]}>
                    <mesh
                        geometry={nodes.lowPol_cell024.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell024_1.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-0.243, -0.555, 0.358]}>
                    <mesh
                        geometry={nodes.lowPol_cell025_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell025_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-1.922, 0.083, 0.654]}>
                    <mesh
                        geometry={nodes.lowPol_cell026_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell026_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-2.019, -0.119, 0.041]}>
                    <mesh
                        geometry={nodes.lowPol_cell027_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell027_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[0.074, -0.566, 0.549]}>
                    <mesh
                        geometry={nodes.lowPol_cell028_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell028_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-0.237, 0.023, 0.901]}>
                    <mesh
                        geometry={nodes.lowPol_cell029_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell029_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-0.472, 0.444, 0.783]}>
                    <mesh
                        geometry={nodes.lowPol_cell030_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell030_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-0.874, 0.832, 0.108]}>
                    <mesh
                        geometry={nodes.lowPol_cell031_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell031_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[2.049, -0.537, 0.014]}>
                    <mesh
                        geometry={nodes.lowPol_cell032_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell032_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-1.775, 0.691, -0.322]}>
                    <mesh
                        geometry={nodes.lowPol_cell033_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell033_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[1.811, 0.57, -0.303]}>
                    <mesh
                        geometry={nodes.lowPol_cell034_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell034_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-0.489, -0.571, 0.186]}>
                    <mesh
                        geometry={nodes.lowPol_cell035_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell035_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[1.914, 0.683, 0.302]}>
                    <mesh
                        geometry={nodes.lowPol_cell036_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell036_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[0.407, -0.68, -0.193]}>
                    <mesh
                        geometry={nodes.lowPol_cell037_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell037_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[1.295, 0.796, 0.052]}>
                    <mesh
                        geometry={nodes.lowPol_cell038_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell038_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[0.65, 0.874, 0.034]}>
                    <mesh
                        geometry={nodes.lowPol_cell039_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell039_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-1.884, 0.01, -0.619]}>
                    <mesh
                        geometry={nodes.lowPol_cell040_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell040_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[0.359, -0.635, 0.555]}>
                    <mesh
                        geometry={nodes.lowPol_cell041_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell041_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[0.985, 0.149, -0.745]}>
                    <mesh
                        geometry={nodes.lowPol_cell042_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell042_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-0.167, -0.381, -0.49]}>
                    <mesh
                        geometry={nodes.lowPol_cell043_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell043_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[-1.306, 0.037, 0.807]}>
                    <mesh
                        geometry={nodes.lowPol_cell044_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell044_2.geometry}
                        material={insideMat}
                    />
                </group>
                <group position={[1.925, -0.007, 0.475]}>
                    <mesh
                        geometry={nodes.lowPol_cell045_1.geometry}
                        material={outsideMat}
                    />
                    <mesh
                        geometry={nodes.lowPol_cell045_2.geometry}
                        material={insideMat}
                    />
                </group>
            </group>
        </group >
    );
}

useGLTF.preload(require("../models/cyrus_cylinder_low_poly_version_6.glb"));
useLoader.preload(TextureLoader, require('../textures/cylinder_diffuse_baked_version_6_optim.jpg'))